import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

// Components
import { Mui } from 'components';

// Local components
import BannerItem from './BannerItem';

const INTERVAL_TIME = 100;
const TOTAL_DURATION = 5000;

function BannerCarousel({ type, slug, banners, onInteraction, ...rest }) {
  // States
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [progress, setProgress] = useState(0);
  const elapsedRef = useRef(0);

  // Constants
  const safeIndex = currentIndex >= banners.length ? 0 : currentIndex;
  const currentBanner = banners[safeIndex];

  // Actions
  const handleNext = () => {
    elapsedRef.current = 0;
    setProgress(0);
    setCurrentIndex(prev => (prev + 1) % banners.length);
  };

  const handlePrev = () => {
    elapsedRef.current = 0;
    setProgress(0);
    setCurrentIndex(prev => (prev - 1 + banners.length) % banners.length);
  };

  // Effects
  useEffect(() => {
    if (banners.length < 2) {
      elapsedRef.current = 0;
      setProgress(0);
      return () => {};
    }

    if (isHovered) return () => {};

    const timer = setInterval(() => {
      elapsedRef.current += INTERVAL_TIME;

      if (elapsedRef.current >= TOTAL_DURATION) {
        elapsedRef.current = 0;
        setProgress(0);
        setCurrentIndex(current => (current + 1) % banners.length);
      } else {
        setProgress((elapsedRef.current / TOTAL_DURATION) * 100);
      }
    }, INTERVAL_TIME);

    return () => clearInterval(timer);
  }, [banners.length, isHovered]);

  if (!currentBanner) return null;

  return (
    <Mui.Box
      width="100%"
      {...rest}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <BannerItem
        key={currentBanner.uid}
        type={type}
        slug={slug}
        banner={currentBanner}
        onInteraction={onInteraction}
        totalBanners={banners.length}
        currentIndex={safeIndex}
        progress={banners.length > 1 ? progress : 0}
        onNext={handleNext}
        onPrev={handlePrev}
      />
    </Mui.Box>
  );
}

BannerCarousel.propTypes = {
  type: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  banners: PropTypes.array.isRequired,
  onInteraction: PropTypes.func.isRequired,
};

export default BannerCarousel;
